import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Loader2 } from "lucide-react";
import type { Lang } from "@/content/tracks";

type PackageSlug = "strategic-mentoring" | "turnaround-sprint" | "board-readiness";
type ModalLang = "en" | "fr";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packageSlug: PackageSlug;
  packageName: { en: string; fr: string };
  lang: Lang;
}

const COPY: Record<ModalLang, {
  title: (name: string) => string;
  subtitle: string;
  section_identity: string;
  name_label: string;
  email_label: string;
  company_label: string;
  role_label: string;
  section_context: string;
  stage_label: string;
  stage_options: Record<string, string>;
  revenue_label: string;
  revenue_options: Record<string, string>;
  team_label: string;
  team_options: Record<string, string>;
  country_label: string;
  lang_label: string;
  section_application: string;
  figuring_out_label: string;
  figuring_out_placeholder: string;
  why_package_label: string;
  why_package_placeholder: string;
  success_label: string;
  success_placeholder: string;
  section_contact: string;
  phone_label: string;
  linkedin_label: string;
  how_heard_label: string;
  submit: string;
  submitting: string;
  success_title: string;
  success_body: string;
  close: string;
  error_generic: string;
  required: string;
  invalid_email: string;
  too_short: (n: number) => string;
}> = {
  en: {
    title: (n) => `Apply for ${n}`,
    subtitle: "Bruno reviews every application personally. 5 business days to hear back.",
    section_identity: "About you",
    name_label: "Full name *",
    email_label: "Email *",
    company_label: "Company name *",
    role_label: "Role / title *",
    section_context: "Business context",
    stage_label: "Current stage",
    stage_options: {
      "": "—",
      pre_revenue: "Pre-revenue",
      post_revenue_seed: "Post-revenue, seed",
      post_pmf: "Post-PMF",
      scaling: "Scaling",
      pre_transaction: "Pre-transaction",
    },
    revenue_label: "Monthly revenue",
    revenue_options: {
      "": "—",
      under_10k: "Under €10k",
      "10k_50k": "€10k–50k",
      "50k_250k": "€50k–250k",
      "250k_1m": "€250k–1M",
      "1m_plus": "€1M+",
    },
    team_label: "Team size",
    team_options: {
      "": "—",
      "1_5": "1–5",
      "6_20": "6–20",
      "21_50": "21–50",
      "51_100": "51–100",
      "100_plus": "100+",
    },
    country_label: "Country / primary market",
    lang_label: "Preferred language",
    section_application: "Your application",
    figuring_out_label: "What are you trying to figure out or build? *",
    figuring_out_placeholder: "The specific problem, decision, or transformation you're working through.",
    why_package_label: "Why this package specifically? *",
    why_package_placeholder: "What about this engagement structure fits your situation?",
    success_label: "What does success look like 12 months from now? *",
    success_placeholder: "What's the outcome you're working toward?",
    section_contact: "Optional — easier to reach you",
    phone_label: "Phone / WhatsApp",
    linkedin_label: "LinkedIn or website URL",
    how_heard_label: "How did you hear about Bootpreneurs?",
    submit: "Submit application",
    submitting: "Sending...",
    success_title: "Application received.",
    success_body: "Bruno will review this within 5 business days. You'll hear from him either way — yes, no, or 'let's talk about something else.' Watch your inbox.",
    close: "Close",
    error_generic: "Something went wrong. Try again in a moment.",
    required: "Required",
    invalid_email: "Please enter a valid email",
    too_short: (n) => `At least ${n} characters required`,
  },
  fr: {
    title: (n) => `Candidater pour ${n}`,
    subtitle: "Bruno examine chaque candidature personnellement. Réponse sous 5 jours ouvrés.",
    section_identity: "Qui êtes-vous",
    name_label: "Nom complet *",
    email_label: "Email *",
    company_label: "Nom de l'entreprise *",
    role_label: "Fonction / titre *",
    section_context: "Contexte business",
    stage_label: "Stade actuel",
    stage_options: {
      "": "—",
      pre_revenue: "Avant revenus",
      post_revenue_seed: "Post-revenus, seed",
      post_pmf: "Post-PMF",
      scaling: "En scaling",
      pre_transaction: "Pré-transaction",
    },
    revenue_label: "Revenus mensuels",
    revenue_options: {
      "": "—",
      under_10k: "Moins de 10k€",
      "10k_50k": "10k–50k€",
      "50k_250k": "50k–250k€",
      "250k_1m": "250k–1M€",
      "1m_plus": "1M€+",
    },
    team_label: "Taille de l'équipe",
    team_options: {
      "": "—",
      "1_5": "1–5",
      "6_20": "6–20",
      "21_50": "21–50",
      "51_100": "51–100",
      "100_plus": "100+",
    },
    country_label: "Pays / marché principal",
    lang_label: "Langue préférée",
    section_application: "Votre candidature",
    figuring_out_label: "Que cherchez-vous à comprendre ou à construire ? *",
    figuring_out_placeholder: "Le problème précis, la décision, ou la transformation sur laquelle vous travaillez.",
    why_package_label: "Pourquoi ce package spécifiquement ? *",
    why_package_placeholder: "Qu'est-ce qui dans cette structure d'engagement correspond à votre situation ?",
    success_label: "À quoi ressemble le succès dans 12 mois ? *",
    success_placeholder: "Le résultat vers lequel vous travaillez.",
    section_contact: "Optionnel — pour vous joindre plus facilement",
    phone_label: "Téléphone / WhatsApp",
    linkedin_label: "URL LinkedIn ou site web",
    how_heard_label: "Comment avez-vous connu Bootpreneurs ?",
    submit: "Envoyer la candidature",
    submitting: "Envoi...",
    success_title: "Candidature reçue.",
    success_body: "Bruno va l'examiner sous 5 jours ouvrés. Vous aurez de ses nouvelles dans tous les cas — oui, non, ou 'parlons d'autre chose.' Surveillez votre boîte mail.",
    close: "Fermer",
    error_generic: "Un problème est survenu. Réessayez dans un instant.",
    required: "Requis",
    invalid_email: "Veuillez saisir un e-mail valide",
    too_short: (n) => `Au moins ${n} caractères requis`,
  },
};

export default function MentorApplicationModal({ open, onOpenChange, packageSlug, packageName, lang }: Props) {
  const resolvedLang: ModalLang = lang === "fr" ? "fr" : "en";
  const t = COPY[resolvedLang];

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [stage, setStage] = useState("");
  const [revenue, setRevenue] = useState("");
  const [team, setTeam] = useState("");
  const [country, setCountry] = useState("");
  const [preferredLang, setPreferredLang] = useState<ModalLang>(resolvedLang);
  const [figuringOut, setFiguringOut] = useState("");
  const [whyPackage, setWhyPackage] = useState("");
  const [success12, setSuccess12] = useState("");
  const [phone, setPhone] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [howHeard, setHowHeard] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setName(""); setEmail(""); setCompany(""); setRole("");
    setStage(""); setRevenue(""); setTeam(""); setCountry(""); setPreferredLang(resolvedLang);
    setFiguringOut(""); setWhyPackage(""); setSuccess12("");
    setPhone(""); setLinkedin(""); setHowHeard("");
    setSubmitting(false); setSuccess(false); setError(null);
  }

  function handleOpenChange(v: boolean) {
    if (!v) reset();
    onOpenChange(v);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) { setError(`${t.required}: ${t.name_label}`); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError(t.invalid_email); return; }
    if (!company.trim()) { setError(`${t.required}: ${t.company_label}`); return; }
    if (!role.trim()) { setError(`${t.required}: ${t.role_label}`); return; }
    if (figuringOut.trim().length < 200) { setError(t.too_short(200)); return; }
    if (whyPackage.trim().length < 100) { setError(t.too_short(100)); return; }
    if (success12.trim().length < 100) { setError(t.too_short(100)); return; }

    setSubmitting(true);
    try {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/apply-for-mentor`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          package_slug: packageSlug,
          package_name: packageName[resolvedLang] ?? packageName.en,
          customer_name: name.trim(),
          customer_email: email.trim(),
          customer_phone: phone.trim() || null,
          company: company.trim(),
          role: role.trim(),
          stage: stage || null,
          monthly_revenue: revenue || null,
          team_size: team || null,
          country: country.trim() || null,
          current_challenge: figuringOut.trim(),
          why_bruno: whyPackage.trim(),
          expected_outcome: success12.trim(),
          linkedin_url: linkedin.trim() || null,
          how_heard: howHeard.trim() || null,
          preferred_lang: preferredLang,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setSuccess(true);
    } catch (err) {
      console.error("Mentor application submit failed", err);
      setError(t.error_generic);
    } finally {
      setSubmitting(false);
    }
  }

  const renderSelect = (id: string, label: string, value: string, onChange: (v: string) => void, options: Record<string, string>) => (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {Object.entries(options).map(([k, v]) => (
          <option key={k} value={k}>{v}</option>
        ))}
      </select>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        {success ? (
          <div className="py-8 text-center space-y-4">
            <CheckCircle2 className="mx-auto h-14 w-14 text-primary" />
            <DialogHeader>
              <DialogTitle className="text-center">{t.success_title}</DialogTitle>
              <DialogDescription className="text-center text-base leading-relaxed">{t.success_body}</DialogDescription>
            </DialogHeader>
            <Button onClick={() => handleOpenChange(false)} className="mt-4">{t.close}</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t.title(packageName[resolvedLang] ?? packageName.en)}</DialogTitle>
              <DialogDescription>{t.subtitle}</DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Identity */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t.section_identity}</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="ma-name">{t.name_label}</Label>
                    <Input id="ma-name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ma-email">{t.email_label}</Label>
                    <Input id="ma-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={255} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ma-company">{t.company_label}</Label>
                    <Input id="ma-company" value={company} onChange={(e) => setCompany(e.target.value)} required maxLength={200} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ma-role">{t.role_label}</Label>
                    <Input id="ma-role" value={role} onChange={(e) => setRole(e.target.value)} required maxLength={200} />
                  </div>
                </div>
              </div>

              {/* Context */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t.section_context}</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  {renderSelect("ma-stage", t.stage_label, stage, setStage, t.stage_options)}
                  {renderSelect("ma-revenue", t.revenue_label, revenue, setRevenue, t.revenue_options)}
                  {renderSelect("ma-team", t.team_label, team, setTeam, t.team_options)}
                  <div className="space-y-2">
                    <Label htmlFor="ma-country">{t.country_label}</Label>
                    <Input id="ma-country" value={country} onChange={(e) => setCountry(e.target.value)} maxLength={120} />
                  </div>
                  {renderSelect("ma-lang", t.lang_label, preferredLang, (v) => setPreferredLang(v as ModalLang), { en: "English", fr: "Français" })}
                </div>
              </div>

              {/* Application */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t.section_application}</h3>
                <div className="space-y-2">
                  <Label htmlFor="ma-figuring">{t.figuring_out_label}</Label>
                  <Textarea
                    id="ma-figuring"
                    value={figuringOut}
                    onChange={(e) => setFiguringOut(e.target.value)}
                    placeholder={t.figuring_out_placeholder}
                    minLength={200}
                    maxLength={1000}
                    rows={5}
                    required
                  />
                  <p className="text-xs text-muted-foreground">{figuringOut.length}/1000 (min 200)</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ma-why">{t.why_package_label}</Label>
                  <Textarea
                    id="ma-why"
                    value={whyPackage}
                    onChange={(e) => setWhyPackage(e.target.value)}
                    placeholder={t.why_package_placeholder}
                    minLength={100}
                    maxLength={500}
                    rows={4}
                    required
                  />
                  <p className="text-xs text-muted-foreground">{whyPackage.length}/500 (min 100)</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ma-success">{t.success_label}</Label>
                  <Textarea
                    id="ma-success"
                    value={success12}
                    onChange={(e) => setSuccess12(e.target.value)}
                    placeholder={t.success_placeholder}
                    minLength={100}
                    maxLength={500}
                    rows={4}
                    required
                  />
                  <p className="text-xs text-muted-foreground">{success12.length}/500 (min 100)</p>
                </div>
              </div>

              {/* Contact */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t.section_contact}</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="ma-phone">{t.phone_label}</Label>
                    <Input id="ma-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={60} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ma-linkedin">{t.linkedin_label}</Label>
                    <Input id="ma-linkedin" type="url" value={linkedin} onChange={(e) => setLinkedin(e.target.value)} maxLength={500} />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="ma-how">{t.how_heard_label}</Label>
                    <Input id="ma-how" value={howHeard} onChange={(e) => setHowHeard(e.target.value)} maxLength={200} />
                  </div>
                </div>
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" disabled={submitting} className="w-full" size="lg">
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t.submitting}</> : t.submit}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
